import React, { useEffect, useState, useCallback, useRef } from "react"
import { createPortal } from "react-dom"
import { useParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { useAppDispatch, useAppSelector } from "../../../redux/store"
import {
  deleteGuest,
  getContactsOnEvent,
  loadMoreContacts,
  setCurrentPage,
  setLoading,
} from "../../../redux/slices/contacts/contactsSlices"
import { IEvent } from "../../../components/Events/types"
import { IContact } from "../../../components/Contacts/types"
import { useInfiniteScroll } from "../hooks/useInfiniteScroll"
import { getUnifiedStatus } from "./GuestList"
import ThankYouComposeDrawer from "./ThankYouComposeDrawer"
import {
  type InvitationStatusFilter,
  type ThankStatusFilter,
  INVITATION_FILTERS,
  THANK_FILTERS,
  matchesInvitationFilter,
  matchesStatusWithFailureType,
} from "../constants/filters"
import styles from "../EventDashboardV2.module.scss"

interface ThankYouGuestListProps {
  event: IEvent
  invitationFilter: InvitationStatusFilter
  thankFilter: ThankStatusFilter
  onInvitationFilterChange: (filter: InvitationStatusFilter) => void
  onThankFilterChange: (filter: ThankStatusFilter) => void
  editPopupOpen: (guest: any) => void
  filter: any
  notify: (text: string, isError: boolean) => void
}

const SearchIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <line x1="21" y1="21" x2="16.65" y2="16.65" />
  </svg>
)

const EditSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
)

const WhatsAppSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
)

const MailSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
    <polyline points="22,6 12,13 2,6" />
  </svg>
)

const TrashSmallIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
)

const ChevronDownIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="12" height="12">
    <polyline points="6 9 12 15 18 9" />
  </svg>
)

const CheckCircleIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="16" height="16">
    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
    <polyline points="22 4 12 14.01 9 11.01" />
  </svg>
)

const getThankStatus = (
  guest: any,
  dateLocale: string,
): { text: string; variant: string; dotColor?: string; date?: string } => {
  if (guest.thank_status === "sent") {
    const date = guest.thank_sent_at
      ? new Date(guest.thank_sent_at).toLocaleDateString(dateLocale, {
          month: "short",
          day: "numeric",
        })
      : ""
    return { text: "Sent", variant: "Sent", dotColor: "Green", date }
  }
  if (guest.thank_status === "failed") {
    return { text: "Failed", variant: "Failed", dotColor: "Red" }
  }
  return { text: "Not sent", variant: "NotSent" }
}

const DeleteButton: React.FC<{
  guestId: number
  guestName: string
  eventId: string
  filter: any
  notify: (text: string, isError: boolean) => void
}> = ({ guestId, guestName, eventId, filter, notify }) => {
  const dispatch = useAppDispatch()
  const { t } = useTranslation()
  const [confirming, setConfirming] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const btnRef = useRef<HTMLButtonElement>(null)
  const popRef = useRef<HTMLDivElement>(null)
  const [pos, setPos] = useState({ top: 0, left: 0 })

  useEffect(() => {
    if (!confirming) return
    const onClickOutside = (e: MouseEvent) => {
      if (
        popRef.current && !popRef.current.contains(e.target as Node) &&
        btnRef.current && !btnRef.current.contains(e.target as Node)
      ) {
        setConfirming(false)
      }
    }
    document.addEventListener("mousedown", onClickOutside)
    return () => document.removeEventListener("mousedown", onClickOutside)
  }, [confirming])

  const openConfirm = () => {
    if (btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect()
      setPos({
        top: rect.bottom + 6 + window.scrollY,
        left: rect.right - 240 + window.scrollX,
      })
    }
    setConfirming(true)
  }

  const handleDelete = () => {
    setDeleting(true)
    dispatch(deleteGuest({ eventId, guestId: [{ contact_id: guestId }] }))
      .then((result: any) => {
        if (result.error) {
          notify(t("eventDashboardV2.messages.failedRemoveGuest"), true)
        } else {
          dispatch(setLoading())
          dispatch(getContactsOnEvent(filter))
          notify(t("eventDashboardV2.messages.guestRemoved"), false)
        }
        setDeleting(false)
        setConfirming(false)
      })
      .catch(() => {
        notify(t("eventDashboardV2.messages.somethingWentWrong"), true)
        setDeleting(false)
        setConfirming(false)
      })
  }

  return (
    <>
      <button
        ref={btnRef}
        className={`${styles.actionIconBtn} ${styles.actionIconBtnDanger}`}
        title={t("eventDashboardV2.actions.remove")}
        onClick={openConfirm}
      >
        <TrashSmallIcon />
      </button>
      {confirming && createPortal(
        <div
          ref={popRef}
          className={styles.deleteConfirm}
          style={{ top: pos.top, left: pos.left }}
        >
          <p className={styles.deleteConfirmText}>
            {t("eventDashboardV2.messages.confirmRemoveGuest", { name: guestName })}
          </p>
          <div className={styles.deleteConfirmActions}>
            <button
              type="button"
              className={styles.deleteConfirmCancel}
              onClick={() => setConfirming(false)}
              disabled={deleting}
            >
              {t("eventDashboardV2.actions.cancel")}
            </button>
            <button
              type="button"
              className={styles.deleteConfirmBtn}
              onClick={handleDelete}
              disabled={deleting}
            >
              {deleting ? t("eventDashboardV2.messages.removing") : t("eventDashboardV2.actions.remove")}
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  )
}

const ThankYouGuestList: React.FC<ThankYouGuestListProps> = ({
  event,
  invitationFilter,
  thankFilter,
  onInvitationFilterChange,
  onThankFilterChange,
  editPopupOpen,
  filter: parentFilter,
  notify,
}) => {
  const { t, i18n } = useTranslation()
  const { event_id } = useParams()
  const dispatch = useAppDispatch()
  const { data_uploaded, current_page, total_pages, limit, isLoading } =
    useAppSelector((state) => state.contacts)
  const balance = useAppSelector((state) => state.auth.balance)

  const [searchValue, setSearchValue] = useState("")
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set())
  const [sendPhase, setSendPhase] = useState<"idle" | "compose" | "success">("idle")
  const [sendResult, setSendResult] = useState({ sent: 0, skipped: 0 })

  const activeInv = INVITATION_FILTERS.find((f) => f.id === invitationFilter) ?? INVITATION_FILTERS[0]
  const activeThank = THANK_FILTERS.find((f) => f.id === thankFilter) ?? THANK_FILTERS[0]
  const dateLocale = i18n.language?.toLowerCase().startsWith("ar") ? "ar-SA" : "en-US"

  const filterParams = useCallback(() => ({
    sort: "",
    order: "",
    searchValue,
    id: Number(event_id),
    statusNumber: activeInv.statusNumber,
    waStatusNumber: activeInv.waStatusNumber,
    reminderNumber: "",
  }), [searchValue, event_id, activeInv.statusNumber, activeInv.waStatusNumber])

  const fetchGuests = useCallback(() => {
    dispatch(setCurrentPage(1))
    dispatch(getContactsOnEvent(filterParams()))
  }, [dispatch, filterParams])

  useEffect(() => {
    fetchGuests()
  }, [fetchGuests])

  const handleLoadMore = useCallback(async (page: number) => {
    await dispatch(loadMoreContacts({ page, params: filterParams() }))
  }, [dispatch, filterParams])

  const { sentinelRef, isFetchingMore, hasMore, reset } = useInfiniteScroll({
    totalPages: total_pages,
    onLoadMore: handleLoadMore,
  })

  const handleInvFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onInvitationFilterChange(e.target.value as InvitationStatusFilter)
    reset()
  }

  const handleThankFilterChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    onThankFilterChange(e.target.value as ThankStatusFilter)
    reset()
  }

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value)
    reset()
  }

  const getContactDisplay = (guest: IContact) => {
    const phone = guest.phone ? guest.phone.split("|")[0] : ""
    const hasExtra = guest.phone && guest.phone.includes("|")
    return { phone, email: guest.email, hasExtra }
  }

  const matchesInvFilter = (guest: any): boolean =>
    matchesInvitationFilter(guest, activeInv)

  const matchesThankFilter = (guest: any): boolean =>
    matchesStatusWithFailureType(
      guest,
      "thank_status",
      "thank_failure_type",
      activeThank.id,
    )

  const filteredGuests = data_uploaded.filter(
    (g: any) => matchesInvFilter(g) && matchesThankFilter(g),
  )

  const allSelected =
    filteredGuests.length > 0 &&
    filteredGuests.every((g: any) => selectedIds.has(g.id))

  const handleSelectAll = () => {
    if (allSelected) {
      setSelectedIds(new Set())
    } else {
      setSelectedIds(new Set(filteredGuests.map((g: any) => g.id)))
    }
  }

  const handleSelectRow = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
      } else {
        next.add(id)
      }
      return next
    })
  }

  const localFilter = {
    sort: "",
    order: "",
    searchValue,
    statusNumber: activeInv.statusNumber,
    waStatusNumber: activeInv.waStatusNumber,
    reminderNumber: "",
    id: Number(event_id),
  }

  const hasSelection = selectedIds.size > 0

  // Eligibility rules split by intent:
  //
  //   • With explicit selection — respect operator intent. If they picked
  //     a "Sent" row, they want to re-thank (lost message, follow-up,
  //     correction). Only hard blocker is "no phone number" — nothing
  //     to message over WhatsApp.
  //
  //   • Without selection — conservative bulk default. Limit to guests
  //     who haven't been thanked yet so a stray click on the top-of-list
  //     button can't quietly re-thank everyone in view. Operators who
  //     want to re-thank existing guests must select them explicitly.
  //
  // The old selection branch also excluded `thank_status === "sent"`,
  // which made the Send button silently no-op on sent-status rows even
  // though they were deliberately selected — a dead-button UX bug.
  const hasPhone = (g: any) => !!g.phone
  const eligibleGuests = hasSelection
    ? filteredGuests.filter(
        (g: any) => selectedIds.has(g.id) && hasPhone(g),
      )
    : filteredGuests.filter(
        (g: any) =>
          (g.thank_status === "not_sent" ||
            g.thank_status === "failed" ||
            !g.thank_status) &&
          hasPhone(g),
      )

  const handleSendClick = () => {
    if (eligibleGuests.length === 0) {
      notify(t("eventDashboardV2.messages.noEligibleThankYou"), true)
      return
    }

    const cost = eligibleGuests.filter((g: any) => g.free_reminder === false).length
    if (cost > balance) {
      notify(t("eventDashboardV2.messages.topUpBalance"), true)
      return
    }

    setSendPhase("compose")
  }

  const handleComposeSuccess = (sent: number, skipped: number) => {
    setSendResult({ sent, skipped })
    setSendPhase("success")
    setSelectedIds(new Set())
    dispatch(setLoading())
    fetchGuests()
    setTimeout(() => setSendPhase("idle"), 4000)
  }

  const handleComposeClose = () => {
    setSendPhase("idle")
  }

  return (
    <div className={styles.guestListSection}>
      {sendPhase === "success" && (
        <div className={styles.sendSuccessBanner}>
          <CheckCircleIcon />
          <span>
            {t("eventDashboardV2.messages.thankYouSent", { count: sendResult.sent })}
            {sendResult.skipped > 0 && ` · ${t("eventDashboardV2.messages.skipped", { count: sendResult.skipped })}`}
          </span>
        </div>
      )}

      <div className={styles.guestListHeader}>
        <div className={styles.searchInput}>
          <SearchIcon />
          <input
            type="text"
            placeholder={t("eventDashboardV2.actions.search")}
            value={searchValue}
            onChange={handleSearch}
          />
        </div>
        {!hasSelection && (
          <div className={styles.guestListControls}>
            <div className={styles.filterSelectWrap}>
              <select
                className={styles.filterSelect}
                value={invitationFilter}
                onChange={handleInvFilterChange}
              >
                {INVITATION_FILTERS.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.id === "all" ? t("eventDashboardV2.table.invitationStatus") : t(`eventDashboardV2.filters.${opt.id}`, opt.label)}
                  </option>
                ))}
              </select>
              <ChevronDownIcon />
            </div>
            <div className={styles.filterSelectWrap}>
              <select
                className={styles.filterSelect}
                value={thankFilter}
                onChange={handleThankFilterChange}
              >
                {THANK_FILTERS.map((opt) => (
                  <option key={opt.id} value={opt.id}>
                    {opt.id === "all" ? t("eventDashboardV2.messages.thankYouStatus") : t(`eventDashboardV2.filters.${opt.id}`, opt.label)}
                  </option>
                ))}
              </select>
              <ChevronDownIcon />
            </div>
            <button
              className={styles.sendReminderBtn}
              onClick={handleSendClick}
              disabled={eligibleGuests.length === 0}
              title={
                eligibleGuests.length === 0
                  ? t("eventDashboardV2.messages.noEligibleThankYouTooltip")
                  : undefined
              }
            >
              {t("eventDashboardV2.actions.sendThankYouMessage")}
            </button>
          </div>
        )}
      </div>

      <div className={styles.tableWrap}>
        <table className={styles.table}>
          <thead className={styles.tableHead}>
            <tr>
              <th className={styles.checkboxCell}>
                <input
                  type="checkbox"
                  className={styles.checkbox}
                  checked={allSelected}
                  onChange={handleSelectAll}
                />
              </th>
              <th className={styles.colId}>{t("eventDashboardV2.table.id")}</th>
              <th className={styles.colName}>{t("eventDashboardV2.table.guestName")}</th>
              <th className={styles.colContact}>{t("eventDashboardV2.table.contact")}</th>
              <th className={styles.colStatus}>{t("eventDashboardV2.table.invitationStatus")}</th>
              <th className={styles.colStatus}>{t("eventDashboardV2.labels.thankYouStatus")}</th>
              <th className={styles.colActions}>{t("eventDashboardV2.table.actions")}</th>
            </tr>
          </thead>
          <tbody className={styles.tableBody}>
            {isLoading
              ? Array.from({ length: 3 }).map((_, i) => (
                  <tr key={`skel-${i}`}>
                    <td className={styles.checkboxCell}>
                      <div className={styles.skeleton} style={{ width: 16, height: 16 }} />
                    </td>
                    <td><div className={styles.skeleton} style={{ width: 48 }} /></td>
                    <td><div className={styles.skeleton} style={{ width: '70%' }} /></td>
                    <td><div className={styles.skeleton} style={{ width: '60%' }} /></td>
                    <td><div className={styles.skeleton} style={{ width: '50%' }} /></td>
                    <td><div className={styles.skeleton} style={{ width: '50%' }} /></td>
                    <td>
                      <div className={styles.actionsCell}>
                        <div className={styles.skeleton} style={{ width: 32, height: 32 }} />
                        <div className={styles.skeleton} style={{ width: 32, height: 32 }} />
                      </div>
                    </td>
                  </tr>
                ))
              : filteredGuests.map((guest: any) => {
                  const contact = getContactDisplay(guest)
                  const invStatus = getUnifiedStatus(
                    guest.status,
                    guest.whatsapp_invitation_status ?? 0,
                    guest.invitation_failure_type,
                    event.is_open_event === 1,
                  )
                  const thankStatus = getThankStatus(guest, dateLocale)
                  const guestName = [guest.suffix, guest.first_name, guest.last_name]
                    .filter(Boolean)
                    .join(" ")

                  return (
                    <tr key={guest.id}>
                      <td className={styles.checkboxCell}>
                        <input
                          type="checkbox"
                          className={styles.checkbox}
                          checked={selectedIds.has(guest.id)}
                          onChange={() => handleSelectRow(guest.id)}
                        />
                      </td>
                      <td>{guest.id}</td>
                      <td className={styles.guestName}>{guestName}</td>
                      <td>
                        <div className={styles.contactCell}>
                          {contact.phone ? (
                            <>
                              <WhatsAppSmallIcon />
                              {contact.phone}
                            </>
                          ) : contact.email ? (
                            <>
                              <MailSmallIcon />
                              {contact.email}
                            </>
                          ) : (
                            "—"
                          )}
                          {contact.hasExtra && (
                            <span className={styles.contactBadge}>+1</span>
                          )}
                        </div>
                      </td>
                      <td>
                        <span
                          className={`${styles.statusBadge} ${styles[`statusBadge${invStatus.variant}`]}`}
                        >
                          <span
                            className={`${styles.statusDot} ${styles[`statusDot${invStatus.dotColor}`]}`}
                          />
                          {t(`eventDashboardV2.status.${invStatus.text}`, invStatus.text)}
                        </span>
                      </td>
                      <td>
                        <span
                          className={`${styles.statusBadge} ${styles[`statusBadge${thankStatus.variant}`]}`}
                        >
                          {thankStatus.dotColor && (
                            <span
                              className={`${styles.statusDot} ${styles[`statusDot${thankStatus.dotColor}`]}`}
                            />
                          )}
                          {t(`eventDashboardV2.status.${thankStatus.text}`, thankStatus.text)}
                          {thankStatus.date && (
                            <span className={styles.statusDate}> &middot; {thankStatus.date}</span>
                          )}
                        </span>
                      </td>
                      <td>
                        <div className={styles.actionsCell}>
                          <button
                            className={styles.actionIconBtn}
                            title={t("eventDashboardV2.actions.edit")}
                            onClick={() => editPopupOpen(guest)}
                          >
                            <EditSmallIcon />
                          </button>
                          <DeleteButton
                            guestId={guest.id}
                            guestName={guestName}
                            eventId={String(event_id)}
                            filter={localFilter}
                            notify={notify}
                          />
                        </div>
                      </td>
                    </tr>
                  )
                })}
          </tbody>
        </table>
      </div>

      {filteredGuests.length === 0 && !isLoading && (
        <div className={styles.emptyState}>{t("eventDashboardV2.messages.noGuestsFound")}</div>
      )}

      {isFetchingMore && (
        <div className={styles.loadMoreRow}>
          <div className={styles.skeleton} style={{ width: "100%", height: 40 }} />
        </div>
      )}
      {hasMore && <div ref={sentinelRef} className={styles.scrollSentinel} />}

      {hasSelection && sendPhase !== "success" && (
        <div className={styles.selectionBar}>
          <div className={styles.selectionBarLeft}>
            <span className={styles.selectionBadge}>{selectedIds.size}</span>
            <span className={styles.selectionLabel}>
              {t("eventDashboardV2.messages.guestsSelected", { count: selectedIds.size })}
            </span>
            <button
              type="button"
              className={styles.selectionClear}
              onClick={() => setSelectedIds(new Set())}
            >
              {t("eventDashboardV2.actions.clear")}
            </button>
          </div>
          <div className={styles.selectionBarRight}>
            <button
              type="button"
              className={styles.selectionSendBtn}
              onClick={handleSendClick}
              disabled={eligibleGuests.length === 0}
              title={
                eligibleGuests.length === 0
                  ? t("eventDashboardV2.messages.selectedGuestsNoPhone")
                  : undefined
              }
            >
              {t("eventDashboardV2.actions.sendThankYouMessage")}
            </button>
          </div>
        </div>
      )}

      <ThankYouComposeDrawer
        show={sendPhase === "compose"}
        onClose={handleComposeClose}
        eligibleIds={eligibleGuests.map((g: any) => g.id)}
        totalSelected={hasSelection ? selectedIds.size : filteredGuests.length}
        onSuccess={handleComposeSuccess}
        notify={notify}
      />
    </div>
  )
}

export default ThankYouGuestList
